import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { UserFlagsDisplayFlag } from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import {
  XING_JOBS_SEARCH_KI_PATH,
  XING_JOBS_SEARCH_PATH,
} from '@xing-com/crate-core-routes';
import { AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM } from '@xing-com/crate-jobs-constants';
import { useFabiAIGeneration } from '@xing-com/crate-jobs-hooks';
import { IconSearch } from '@xing-com/icons';
import { Container, Section } from '@xing-com/layout-primitives';
import { BodyCopy } from '@xing-com/typography';

import { PageSection } from '../components/page-section';
import type { XingIdNonError } from '../xing-id';

import { SwopCardInspire } from './swop-card-inspire';
import {
  StyledViewJobsButton,
  StyledCardInspireChildrenContainer,
  StyledSkeletonBodyCopy,
} from './swop.styles';

type SWOPType = {
  xingId: XingIdNonError;
};

/**
 * SWOP === Search with profile other https://new-work.atlassian.net/browse/JSR-5249
 * */
export const SWOP: React.FC<SWOPType> = ({ xingId }) => {
  const [linkToSearch, setLinkToSearch] = useState(XING_JOBS_SEARCH_PATH);
  const [triggerFabiAIGeneration, { data, loading }] = useFabiAIGeneration();
  const { track: trackNwt } = useTracking();
  const userName = xingId?.displayName;
  const company = xingId?.occupations?.[0]?.headline;
  const role = xingId?.occupations?.[0]?.subline;
  const userFlag = xingId?.userFlags?.displayFlag;
  const isAmbassadorOrInsider =
    userFlag === UserFlagsDisplayFlag.Ambassador ||
    userFlag === UserFlagsDisplayFlag.Insider;

  const goToSearch = (): void => {
    trackNwt({
      element: 'view_all_jobs',
      elementDetail: 'swop_matching_jobs',
      event: 'opened',
      eventSchema: 'basic',
      page: 'profile/other/details',
      sentBy: 'profile',
      type: 'nwt',
    });
    window.open(linkToSearch, '_blank');
  };

  useEffect(() => {
    if (!isAmbassadorOrInsider) {
      triggerFabiAIGeneration(role || '', company || '');
    }
  }, [company, isAmbassadorOrInsider, role, triggerFabiAIGeneration]);

  useEffect(() => {
    if (data) {
      const params = new URLSearchParams({
        keywords: data.query,
        [AI_SEARCH_FEEDBACK_SURVEY_LO_SWIMP_PARAM]: '1',
      });

      setLinkToSearch(`${XING_JOBS_SEARCH_KI_PATH}?${params.toString()}`);
    }
  }, [data]);

  if (isAmbassadorOrInsider || (!loading && !data)) {
    return null;
  }

  return (
    <React.Fragment>
      <PageSection>
        <Section>
          <Container>
            <SwopCardInspire>
              <StyledCardInspireChildrenContainer>
                {loading ? (
                  <StyledSkeletonBodyCopy amountRows={2} />
                ) : (
                  <BodyCopy>
                    <FormattedMessage
                      id="PROFILE_SWIMP_BANNER_DESCRIPTION"
                      values={{ username: userName, role: <b>{role}</b> }}
                    />
                  </BodyCopy>
                )}
                <StyledViewJobsButton
                  icon={IconSearch}
                  loading={loading}
                  onClick={goToSearch}
                  size="small"
                  variant="onColorStrong"
                >
                  <FormattedMessage id="PROFILE_SWIMP_BANNER_CTA" />
                </StyledViewJobsButton>
              </StyledCardInspireChildrenContainer>
            </SwopCardInspire>
          </Container>
        </Section>
      </PageSection>
    </React.Fragment>
  );
};
